import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Home, Heart, Bell, Calendar, MapPin, Loader2, ShieldCheck, Target, Clock, FileText, CheckCircle2, Circle, TrendingUp } from "lucide-react";
import { API } from "./api";

const fmt = (n) => { try { return `${parseInt(n).toLocaleString("fr-FR")} €`; } catch { return n; } };
const dt = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "");

export default function AcquereurPortal() {
  const { token } = useParams();
  const [d, setD] = useState(undefined);
  const load = () => fetch(`${API}/acquereur/portal/${token}`).then((r) => (r.ok ? r.json() : null)).then(setD).catch(() => setD(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (d === undefined) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#C19A6B] border-t-transparent rounded-full animate-spin" /></div>;
  if (d === null) return <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center"><p className="text-zinc-400">Lien invalide ou expiré.</p></div>;

  const a = d.acquereur;
  const favs = new Set(d.favorite_slugs || []);
  const toggleFav = async (slug) => {
    try {
      const res = await fetch(`${API}/acquereur/portal/${token}/favorite`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ slug }) });
      if (res.ok) load();
    } catch (_) {}
  };
  const Card = ({ icon: Ic, title, count, children, testid }) => (
    <div className="border border-white/10 bg-[#0d0d10] p-5" data-testid={testid}>
      <h3 className="text-white text-sm font-medium flex items-center gap-2 mb-3"><Ic className="w-4 h-4 text-[#C19A6B]" /> {title}{count != null && <span className="text-zinc-500">({count})</span>}</h3>
      {children}
    </div>
  );
  const Listing = ({ l }) => (
    <div className="border border-white/10 overflow-hidden relative" data-testid={`acq-listing-${l.slug}`}>
      {l.cover && <img src={l.cover} alt="" className="w-full h-32 object-cover" />}
      <button onClick={() => toggleFav(l.slug)} className="absolute top-2 right-2 bg-black/60 rounded-full p-2" data-testid={`fav-${l.slug}`}>
        <Heart className={`w-4 h-4 ${favs.has(l.slug) ? "fill-[#C19A6B] text-[#C19A6B]" : "text-white"}`} />
      </button>
      <div className="p-3">
        <p className="text-white text-sm font-medium truncate">{l.title}</p>
        <p className="text-xs text-zinc-500">{[l.type, l.surface ? `${l.surface} m²` : null, l.city].filter(Boolean).join(" • ")}</p>
        {l.price ? <p className="text-[#C19A6B] text-sm mt-1">{fmt(l.price)}</p> : null}
        <a href={`/bien/${l.slug}`} className="text-xs text-[#C19A6B] hover:underline">Voir l'annonce →</a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-16 pb-20 px-5" data-testid="acquereur-portal">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center gap-2 text-[#C19A6B]"><Home className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.25em]">Espace acquéreur OFF MARKETTE</span></div>
        <div><h1 className="text-2xl font-light">Bonjour {a.name}</h1><p className="text-sm text-zinc-400">Voici votre projet immobilier et les biens qui vous correspondent.</p></div>

        {d.alerts.length > 0 && (
          <div className="border border-[#C19A6B]/40 bg-[#C19A6B]/10 p-4 flex items-center gap-3" data-testid="acq-alerts">
            <Bell className="w-5 h-5 text-[#C19A6B]" />
            <p className="text-sm text-white">{d.alerts[0].label}.</p>
          </div>
        )}

        {/* Frise de suivi de projet acquéreur */}
        {(() => {
          const prog = d.progress || {};
          const steps = [
            { label: "Critères définis", done: true },
            { label: "Biens compatibles", done: (d.compatible || []).length > 0, sub: `${(d.compatible || []).length}` },
            { label: "Visite programmée", done: (d.visits || []).length > 0, sub: (d.visits || []).length ? `${d.visits.length}` : "" },
            { label: "Offre déposée", done: (prog.offers_count || 0) > 0, sub: prog.offers_count ? `${prog.offers_count}` : "" },
            { label: "Compromis / Signature", done: !!prog.has_accepted },
          ];
          const doneCount = steps.filter((s) => s.done).length;
          const pct = Math.round(((doneCount - 1) / (steps.length - 1)) * 100);
          return (
            <Card icon={TrendingUp} title="Suivi de mon projet" testid="acq-timeline">
              <div className="relative pt-2 pb-1">
                <div className="absolute left-3 right-3 top-[18px] h-[2px] bg-white/10" />
                <div className="absolute left-3 top-[18px] h-[2px] bg-[#C19A6B] transition-all duration-500" style={{ width: `calc((100% - 24px) * ${Math.max(0, pct) / 100})` }} />
                <div className="relative flex justify-between">
                  {steps.map((s, i) => (
                    <div key={i} className="flex flex-col items-center text-center flex-1" data-testid={`acq-step-${i}`}>
                      {s.done ? <CheckCircle2 className="w-6 h-6 text-[#C19A6B] bg-[#0d0d10] rounded-full" /> : <Circle className="w-6 h-6 text-zinc-600 bg-[#0d0d10] rounded-full" />}
                      <span className={`mt-2 text-[10px] sm:text-xs leading-tight px-1 ${s.done ? "text-white" : "text-zinc-500"}`}>{s.label}</span>
                      {s.sub && <span className="text-[9px] text-[#C19A6B]/80 mt-0.5">{s.sub}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          );
        })()}

        <Card icon={Target} title="Mes critères de recherche" testid="acq-criteria">
          <div className="space-y-1.5 text-sm text-zinc-300">
            {a.budget && <p><span className="text-zinc-500">Budget :</span> {a.budget}</p>}
            {a.type_bien.length > 0 && <p><span className="text-zinc-500">Type :</span> {a.type_bien.join(", ")}</p>}
            {a.secteurs.length > 0 && <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-zinc-500" /> {a.secteurs.join(", ")}</p>}
            {a.criteres.length > 0 && <p className="text-zinc-400">{a.criteres.join(" · ")}</p>}
            {a.summary && <p className="text-zinc-400 italic pt-1">« {a.summary} »</p>}
          </div>
        </Card>

        <Card icon={Home} title="Annonces compatibles" count={d.compatible.length} testid="acq-compatible">
          {d.compatible.length === 0 ? <p className="text-sm text-zinc-500">Aucune annonce compatible pour l'instant — vous serez alerté dès qu'un bien correspond.</p> : (
            <div className="grid sm:grid-cols-2 gap-3">{d.compatible.map((l) => <Listing key={l.slug} l={l} />)}</div>
          )}
        </Card>

        <Card icon={Heart} title="Mes favoris" count={d.favorites.length} testid="acq-favorites">
          {d.favorites.length === 0 ? <p className="text-sm text-zinc-500">Ajoutez des biens en favori avec le cœur.</p> : (
            <div className="grid sm:grid-cols-2 gap-3">{d.favorites.map((l) => <Listing key={l.slug} l={l} />)}</div>
          )}
        </Card>

        <Card icon={Calendar} title="Visites programmées" count={d.visits.length} testid="acq-visits">
          {d.visits.length === 0 ? <p className="text-sm text-zinc-500">Aucune visite programmée pour l'instant.</p> : (
            <div className="space-y-2">{d.visits.map((v) => (
              <div key={v.id} className="flex items-center justify-between border border-white/10 p-2.5 text-sm gap-2">
                <span className="text-zinc-200 min-w-0">{v.title} <span className="text-zinc-500">· {dt(v.date)} {v.time}</span>{v.listing_title && <span className="block text-xs text-[#C19A6B]/90 mt-0.5">🏠 {v.listing_title}{v.listing_city ? ` · ${v.listing_city}` : ""}</span>}</span>
                <span className="flex items-center gap-2 shrink-0">
                  <a href={`${API}/acquereur/portal/${token}/visit/${v.id}/debrief`} target="_blank" rel="noopener noreferrer" title="Compte-rendu PDF" data-testid={`acq-visit-debrief-${v.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10 rounded px-2 py-1 transition-colors"><FileText className="w-3 h-3" /> CR</a>
                  <span className="text-[10px] uppercase text-zinc-400 border border-white/15 rounded px-1.5 py-0.5 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{v.status === "confirmee" ? "Confirmée" : "Prévue"}</span>
                </span>
              </div>
            ))}</div>
          )}
        </Card>

        <p className="text-[11px] text-zinc-600 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#C19A6B]" /> Accès personnel sécurisé, réservé à votre projet immobilier.</p>
      </div>
    </div>
  );
}
